/**
 * Affiliate Applications Tab
 *
 * Lists applications filtered by status; each pending application can be
 * approved (optional rate + valid_until) or rejected (optional note).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import {
  affiliateApi,
  type AffiliateApplication,
  type AffiliateApplicationStatus,
} from '@/lib/api/affiliate';
import { parseBackendDateTime } from '@/lib/format/datetime';

function statusVariant(
  status: AffiliateApplicationStatus,
): 'warning' | 'success' | 'destructive' {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'destructive';
    default:
      return 'warning';
  }
}

function formatDate(ts: string): string {
  return parseBackendDateTime(ts).toLocaleString();
}

export function AffiliateApplicationsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<AffiliateApplicationStatus>('pending');
  const [activeForm, setActiveForm] = useState<{ id: string; mode: 'approve' | 'reject' } | null>(
    null,
  );
  const [rate, setRate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['affiliate-applications', status],
    queryFn: () => affiliateApi.getApplications(status),
  });

  const closeForm = () => {
    setActiveForm(null);
    setRate('');
    setValidUntil('');
    setNote('');
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['affiliate-applications'] });
    queryClient.invalidateQueries({ queryKey: ['affiliate-affiliates'] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, commission_rate, valid_until }: {
      id: string;
      commission_rate?: number;
      valid_until?: string;
    }) => affiliateApi.approveApplication(id, { commission_rate, valid_until }),
    onSuccess: () => {
      invalidate();
      toast.success(t('affiliatePage.applicationApproved'));
      closeForm();
    },
    onError: (err: Error) => {
      toast.error(`${t('affiliatePage.actionFailed')}: ${err.message}`);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, noteText }: { id: string; noteText?: string }) =>
      affiliateApi.rejectApplication(id, { note: noteText }),
    onSuccess: () => {
      invalidate();
      toast.success(t('affiliatePage.applicationRejected'));
      closeForm();
    },
    onError: (err: Error) => {
      toast.error(`${t('affiliatePage.actionFailed')}: ${err.message}`);
    },
  });

  const isMutating = approveMutation.isPending || rejectMutation.isPending;

  const handleConfirm = (id: string) => {
    if (activeForm?.mode === 'approve') {
      const parsedRate = rate.trim() === '' ? undefined : Number(rate);
      approveMutation.mutate({
        id,
        commission_rate: parsedRate,
        valid_until: validUntil ? new Date(validUntil).toISOString() : undefined,
      });
    } else {
      rejectMutation.mutate({ id, noteText: note.trim() || undefined });
    }
  };

  const statusOptions = [
    { value: 'pending', label: t('affiliatePage.statusPending') },
    { value: 'approved', label: t('affiliatePage.statusApproved') },
    { value: 'rejected', label: t('affiliatePage.statusRejected') },
  ];

  const statusLabel = (s: AffiliateApplicationStatus) =>
    s === 'approved'
      ? t('affiliatePage.statusApproved')
      : s === 'rejected'
        ? t('affiliatePage.statusRejected')
        : t('affiliatePage.statusPending');

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('affiliatePage.applicationsTitle')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('affiliatePage.applicationsDesc')}
          </p>
        </div>
        <div className="w-48">
          <Select
            label={t('affiliatePage.filterStatus')}
            value={status}
            onChange={(e) => setStatus(e.target.value as AffiliateApplicationStatus)}
            options={statusOptions}
          />
        </div>
      </div>

      {error && (
        <Card className="mb-4 border-destructive">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
            <p className="text-destructive text-sm">{(error as Error).message}</p>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : !data?.length ? (
        <Card>
          <p className="text-center text-muted-foreground py-8">
            {t('affiliatePage.noApplications')}
          </p>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">{t('affiliatePage.colUserId')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colStatus')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colNote')}</th>
                  <th className="px-4 py-2">{t('affiliatePage.colCreatedAt')}</th>
                  <th className="px-4 py-2 text-right">{t('affiliatePage.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((app: AffiliateApplication) => (
                  <tr key={app.id} className="border-b border-border align-top">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-foreground" title={app.user_id}>
                        {app.user_id}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusVariant(app.status)}>{statusLabel(app.status)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate">
                      {app.note || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {formatDate(app.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {app.status === 'pending' ? (
                        <div className="flex flex-col items-end gap-2">
                          {activeForm?.id === app.id ? (
                            <div className="w-72 rounded-lg border border-border p-3 bg-muted/30">
                              {activeForm.mode === 'approve' ? (
                                <div className="space-y-3">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    max={1}
                                    label={t('affiliatePage.commissionRateLabel')}
                                    value={rate}
                                    onChange={(e) => setRate(e.target.value)}
                                    helperText={t('affiliatePage.commissionRateHelp')}
                                  />
                                  <Input
                                    type="date"
                                    label={t('affiliatePage.validUntilLabel')}
                                    value={validUntil}
                                    onChange={(e) => setValidUntil(e.target.value)}
                                    helperText={t('affiliatePage.validUntilHelp')}
                                  />
                                </div>
                              ) : (
                                <Input
                                  label={t('affiliatePage.rejectNoteLabel')}
                                  value={note}
                                  onChange={(e) => setNote(e.target.value)}
                                  helperText={t('affiliatePage.rejectNoteHelp')}
                                />
                              )}
                              <div className="flex justify-end gap-2 mt-3">
                                <Button variant="outline" size="sm" onClick={closeForm}>
                                  {t('affiliatePage.cancel')}
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  disabled={isMutating}
                                  onClick={() => handleConfirm(app.id)}
                                >
                                  {t('affiliatePage.confirm')}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  closeForm();
                                  setActiveForm({ id: app.id, mode: 'approve' });
                                }}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                {t('affiliatePage.approve')}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  closeForm();
                                  setActiveForm({ id: app.id, mode: 'reject' });
                                }}
                              >
                                <X className="w-4 h-4 mr-1" />
                                {t('affiliatePage.reject')}
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-right text-muted-foreground">—</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={() => refetch()}>
          {t('affiliatePage.refresh')}
        </Button>
      </div>
    </div>
  );
}
